import { apiClient } from "@/lib/api";
import type { ActivityCategory, Translations } from "@/types";

export type CarouselSlideActivitySummary = {
  id: string;
  name: string;
  category: ActivityCategory;
};

export type CarouselSlide = {
  id: string;
  region_id: string;
  title_translations: Translations;
  description_translations: Translations | null;
  image_path: string | null;
  activity_id: string | null;
  activity: CarouselSlideActivitySummary | null;
  show_activity_button: boolean;
  ignore_distance: boolean;
  categories: ActivityCategory[];
  is_enabled: boolean;
  is_draft: boolean;
  starts_at: string | null;
  ends_at: string | null;
  sort_order: number;
  house_ids: string[];
  status: "draft" | "disabled" | "scheduled" | "visible";
  is_currently_visible: boolean;
  created_at: string;
  updated_at: string;
};

/** Full writable fields shared by create and replace (sort_order is reorder-only). */
export type CarouselSlideFields = {
  title_translations: Translations;
  description_translations: Translations | null;
  image_path: string | null;
  activity_id: string | null;
  show_activity_button: boolean;
  ignore_distance: boolean;
  categories: ActivityCategory[];
  is_enabled: boolean;
  is_draft: boolean;
  starts_at: string | null;
  ends_at: string | null;
  house_ids: string[];
};

export type CarouselSlideCreatePayload = CarouselSlideFields & {
  region_id: string;
  /** Client-chosen id so media can be uploaded before the slide row exists. */
  id: string;
};

export const carouselSlideKeys = {
  all: ["admin", "carousel-slides"] as const,
  list: (regionId: string) =>
    [...carouselSlideKeys.all, "list", regionId] as const,
  detail: (slideId: string) =>
    [...carouselSlideKeys.all, "detail", slideId] as const,
};

export const carouselSlideService = {
  list: async (regionId: string): Promise<CarouselSlide[]> => {
    const { data } = await apiClient.get<CarouselSlide[]>("/carousel-slides", {
      params: { region_id: regionId },
    });
    return data;
  },

  get: async (slideId: string): Promise<CarouselSlide> => {
    const { data } = await apiClient.get<CarouselSlide>(
      `/carousel-slides/${slideId}`,
    );
    return data;
  },

  create: async (payload: CarouselSlideCreatePayload): Promise<CarouselSlide> => {
    const { data } = await apiClient.post<CarouselSlide>(
      "/carousel-slides",
      payload,
    );
    return data;
  },

  replace: async (
    slideId: string,
    payload: CarouselSlideFields,
  ): Promise<CarouselSlide> => {
    const { data } = await apiClient.put<CarouselSlide>(
      `/carousel-slides/${slideId}`,
      payload,
    );
    return data;
  },

  reorder: async (
    regionId: string,
    slideIds: string[],
  ): Promise<CarouselSlide[]> => {
    const { data } = await apiClient.put<CarouselSlide[]>(
      "/carousel-slides/reorder",
      { region_id: regionId, slide_ids: slideIds },
    );
    return data;
  },

  delete: async (slideId: string): Promise<void> => {
    await apiClient.delete(`/carousel-slides/${slideId}`);
  },
};
